import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Lock, X } from 'lucide-react';
import { pricingPlans, type CheckoutPlanId } from '../../data/pricingPlans';
import { useCheckout } from '../../hooks/useCheckout';
import { usePlanEntitlement } from '../../hooks/usePlanEntitlement';
interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const ROW: Record<string, string> = {
  neutral: 'border-gray-800 bg-[#0a0a0a]',
  red: 'border-[#FF0000]/60 bg-[#FF0000]/[0.04]',
  gold: 'border-[#FFD700]/50 bg-[#FFD700]/[0.03]'
};
const BADGE: Record<string, string> = {
  neutral: 'bg-gray-800 text-gray-300',
  red: 'bg-[#FF0000] text-white',
  gold: 'bg-[#FFD700] text-black'
};
const CTA: Record<string, string> = {
  neutral: 'border border-gray-700 bg-transparent text-white hover:border-gray-500 hover:bg-white/5',
  red: 'bg-[#FF0000] text-white hover:bg-[#D90000]',
  gold: 'bg-[#FFD700] text-black hover:bg-[#F5C400]'
};
/**
 * The single plan comparison surface for authenticated users. Every plan the
 * checkout supports is listed once, with the user's active plan marked instead
 * of offered again.
 */
export function SubscriptionModal({
  isOpen,
  onClose
}: SubscriptionModalProps) {
  const {
    planId,
    accessLabel,
    statusLabel,
    loading
  } = usePlanEntitlement();
  const {
    onCheckout,
    pendingPlan
  } = useCheckout('plans_modal');
  const activePlanId = planId;
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-1853" data-mp="246zj71ds1y0f">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-6" data-id="element-1854" data-mp="245y8iy0obdj8">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={onClose} className="absolute inset-0 bg-black/85 backdrop-blur-sm" data-id="element-1855" data-mp="apbsilnpxtgn" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="plans-modal-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative z-10 max-h-[92vh] w-full max-w-4xl overflow-y-auto border border-gray-800 bg-[#050505] p-6 sm:p-8" data-id="element-1856" data-mp="onif35n7w710">
            <button type="button" onClick={onClose} aria-label="Close plans" className="absolute right-4 top-4 p-2 text-gray-500 transition-colors duration-150 ease-out hover:bg-white/5 hover:text-white focus:outline-none focus:ring-2 focus:ring-white" data-id="element-1857" data-mp="1ovyndyz3axbu">
              <X size={18} data-id="element-1858" data-mp="23jtdd7lpqnl8" />
            </button>

            <header className="mb-8 max-w-xl pr-10" data-id="element-1859" data-mp="23mpbavxk9c9o">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF0000]" data-id="element-1860" data-mp="27n9cqbp6321m">
                Clearance levels
              </p>
              <h2 id="plans-modal-title" className="mt-2 text-2xl font-black uppercase tracking-tight text-white sm:text-3xl" data-id="element-1861" data-mp="d86p6jjohu1c">
                Plans & Access
              </h2>
              <p className="mt-3 font-mono text-xs leading-relaxed text-gray-400" data-id="element-1862" data-mp="3j1y3e7okt2we">
                {loading ? 'Verifying your current clearance…' : `You are on ${pricingPlans.find(p => p.id === activePlanId)?.label ?? 'FREE'} — ${accessLabel.toLowerCase()} · ${statusLabel}.`}
              </p>
            </header>

            <ul className="space-y-3" data-id="element-1863" data-mp="30d19i4x3ccep">
              {pricingPlans.map(plan => {
            // Single scans are one-time purchases — always re-buyable.
            const isCurrent = plan.id === activePlanId && plan.id !== 'single_scan';
            const isFree = plan.id === 'free';
            return <li key={plan.id} className={`flex flex-col gap-4 border p-5 sm:flex-row sm:items-center sm:justify-between ${ROW[plan.accent]}`} data-id="element-1864" data-mp="35986v9bf8fl1">
                    <div className="min-w-0 sm:flex-1" data-id="element-1865" data-mp="s4cwcg9gadfs">
                      <div className="flex flex-wrap items-center gap-2" data-id="element-1866" data-mp="s2s26rr1swig">
                        <p className="font-mono text-[11px] font-black uppercase tracking-[0.2em] text-white" data-id="element-1867" data-mp="wvcqxev3kjf4">
                          {plan.label}
                        </p>
                        {plan.badge && <span className={`px-2 py-0.5 font-mono text-[9px] font-black uppercase tracking-[0.15em] ${BADGE[plan.accent]}`} data-id="element-1868" data-mp="n61k5yvonque">
                            {plan.badge}
                          </span>}
                      </div>
                      <p className="mt-1.5 font-mono text-[11px] text-gray-500" data-id="element-1869" data-mp="11w8wdwv3kdmn">
                        {plan.name}
                      </p>
                      <p className="mt-3 flex items-start gap-2 text-sm text-gray-300" data-id="element-1870" data-mp="3mjvktoqqk4a3">
                        <Check size={14} strokeWidth={3} aria-hidden="true" className="mt-0.5 shrink-0 text-gray-600" data-id="element-1871" data-mp="3qlqnoxobeppz" />
                        {plan.entitlement}
                      </p>
                      {plan.id === 'yearly' && plan.savings && <p className="mt-2 font-mono text-[10px] uppercase tracking-wide text-[#FFD700]" data-id="element-1872" data-mp="1nb3udw5xcu3l">
                          {plan.savings} vs paying $29.99 monthly ($359.88 a
                          year)
                        </p>}
                    </div>

                    <div className="sm:w-40 sm:shrink-0 sm:text-right" data-id="element-1873" data-mp="19g1pncctdasd">
                      <p className="text-2xl font-black leading-none tracking-tight text-white" data-id="element-1874" data-mp="14f45wvc5o8or">
                        {plan.price}
                      </p>
                      <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-gray-500" data-id="element-1875" data-mp="2yt54rg7pgvou">
                        {plan.billing}
                      </p>
                    </div>

                    <div className="sm:w-44 sm:shrink-0" data-id="element-1876" data-mp="32b2jgpihdiyu">
                      {isCurrent ? <p className="flex items-center justify-center gap-1.5 border border-gray-700 bg-gray-900/60 py-3 font-mono text-[10px] font-black uppercase tracking-[0.15em] text-gray-300" data-id="element-1877" data-mp="36bmg7rz56kkg">
                          <Check size={12} strokeWidth={3} aria-hidden="true" data-id="element-1878" data-mp="3g2h93f6w8iev" />
                          Current plan
                        </p> : isFree ? <p className="py-3 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-gray-600" data-id="element-1879" data-mp="2nd0gagfd2zi4">
                          Included at signup
                        </p> : <button type="button" onClick={() => void onCheckout(plan.id as CheckoutPlanId)} disabled={pendingPlan !== null} aria-busy={pendingPlan === plan.id} className={`w-full py-3 text-[11px] font-black uppercase tracking-wider transition-colors duration-200 ease-out disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505] ${CTA[plan.accent]}`} data-id="element-1880" data-mp="2xbpzqdmwos0h">
                          {pendingPlan === plan.id ? 'Processing…' : plan.cta}
                        </button>}
                    </div>
                  </li>;
          })}
            </ul>

            <p className="mt-6 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-gray-600" data-id="element-1881" data-mp="1qbuib0ljj2d7">
              <Lock size={10} aria-hidden="true" data-id="element-1882" data-mp="3s5bri8pbkba2" /> Secure checkout · Cancel
              anytime
            </p>
          </motion.div>
        </div>}
    </AnimatePresence>;
}